import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import { CrownOutlined, LockOutlined, CloseOutlined } from '@ant-design/icons';
import './upgradeOverlay.less';
import { useGlobalContext } from '@src/context/globalContext';

interface UpgradeOverlayProps {
 
  onDismiss?: () => void;
  isModal?: boolean;
}

export const UpgradeOverlay: React.FC<UpgradeOverlayProps> = ({ 
  onDismiss,
  isModal = false
}) => {
    const {
        DomainDataGlobal: [domainDataGlobal]
    } = useGlobalContext()
  const projectID = location.pathname.split('/').pop()

  const overlayRef = useRef<HTMLDivElement>(null)

  const handleUpgrade = () => {
    window.location.href = `https://${domainDataGlobal?.domain_name}/payment?redirected_from=${encodeURIComponent(`${domainDataGlobal?.video_domain_name}/${projectID}`)}`;

    // window.location.href = `https://${domainDataGlobal?.domain_name}/payment?redirected_from=${domainDataGlobal?.video_domain_name}/${projectID}`
  }

  // Monitor for DOM manipulation attempts
  useEffect(() => {
    if (!overlayRef.current) return

    const restoreOverlay = () => {
      if (overlayRef.current) {
        overlayRef.current.style.visibility = 'visible'
        overlayRef.current.style.display = 'flex'
        overlayRef.current.style.opacity = '1'
        overlayRef.current.style.zIndex = '10000'
        overlayRef.current.className = `upgrade-overlay ${isModal ? 'modal-content' : ''}`
      }
    }

    // Prevent right-click context menu on overlay
    const preventContextMenu = (e: MouseEvent) => {
      if (overlayRef.current && overlayRef.current.contains(e.target as Node)) {
        e.preventDefault()
        return false
      }
    }

    // Prevent keyboard shortcuts that might hide elements
    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
      // Prevent F12, Ctrl+Shift+I, Ctrl+U, etc.
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          (e.ctrlKey && e.key === 'u') ||
          (e.ctrlKey && e.shiftKey && e.key === 'C')) {
        e.preventDefault()
        return false
      }
    }

    // Add event listeners
    document.addEventListener('contextmenu', preventContextMenu)
    document.addEventListener('keydown', preventKeyboardShortcuts)

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target as HTMLElement
          if (target === overlayRef.current) {
            // Check if visibility, display, opacity, or class was modified
            const computedStyle = window.getComputedStyle(target)
            if (computedStyle.visibility === 'hidden' || 
                computedStyle.display === 'none' || 
                computedStyle.opacity === '0' ||
                !target.className.includes('upgrade-overlay')) {
              restoreOverlay()
            }
          }
        } else if (mutation.type === 'childList') {
          // Check if the overlay element was removed
          const removedNodes = Array.from(mutation.removedNodes)
          if (overlayRef.current && removedNodes.includes(overlayRef.current)) {
            // Element was removed, we need to recreate it
            // This is handled by React's re-render, but we can force it
            window.location.reload()
          }
        }
      })
    })

    // Start observing the parent container
    const parentElement = overlayRef.current.parentElement
    if (parentElement) {
      observer.observe(parentElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['style', 'class']
      })
    }

    // Also observe the overlay element itself
    observer.observe(overlayRef.current, {
      attributes: true,
      attributeFilter: ['style', 'class']
    })

    // More frequent checks for style changes
    const interval = setInterval(() => {
      if (overlayRef.current) {
        const computedStyle = window.getComputedStyle(overlayRef.current)
        if (computedStyle.visibility === 'hidden' || 
            computedStyle.display === 'none' || 
            computedStyle.opacity === '0' ||
            !overlayRef.current.className.includes('upgrade-overlay')) {
          restoreOverlay()
        }
      } else {
        // Element was completely removed, reload page
        window.location.reload()
      }
    }, 50)

    // Additional protection - check if element exists in DOM
    const domCheckInterval = setInterval(() => {
      if (!document.contains(overlayRef.current)) {
        window.location.reload()
      }
    }, 200)

    return () => {
      observer.disconnect()
      clearInterval(interval)
      clearInterval(domCheckInterval)
      document.removeEventListener('contextmenu', preventContextMenu)
      document.removeEventListener('keydown', preventKeyboardShortcuts)
    }
  }, [isModal])
  return (
    <div ref={overlayRef} className={`upgrade-overlay ${isModal ? 'modal-content' : ''}`}>
      <div className="upgrade-card">
        {isModal && onDismiss && (
          <button 
            className="close-button"
            onClick={onDismiss}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        )}
        <div className="upgrade-content">
          <div className="upgrade-icon">
            <CrownOutlined />
          </div>
          <h3 className="upgrade-title">Upgrade to Unlock Editing Features</h3>
          <p className="upgrade-description">
            You&apos;re currently using the free version. Upgrade to access advanced editing features, 
            custom templates, and premium assets.
          </p>
          <div className="upgrade-features">
            <div className="feature-item">
              <LockOutlined className="feature-icon" />
              <span>Premium voice options</span>
            </div>
            <div className="feature-item">
              <LockOutlined className="feature-icon" />
              <span>Script modifications</span>
            </div>
            <div className="feature-item">
              <LockOutlined className="feature-icon" />
              <span>Custom sound effects</span>
            </div>
            <div className="feature-item">
              <LockOutlined className="feature-icon" />
              <span>Voice cloning</span>
            </div>
            <div className="feature-item">
              <LockOutlined className="feature-icon" />
              <span>Advanced captions</span>
            </div>
            <div className="feature-item">
              <LockOutlined className="feature-icon" />
              <span>CTA templates</span>
            </div>
           
           
          </div>
          <div className="upgrade-actions">
            <Button 
              type="primary" 
              size="large"
              icon={<CrownOutlined />}
              onClick={handleUpgrade}
              className="upgrade-button"
            >
              Upgrade Now
            </Button>
           
          </div>
        </div>
      </div>
    </div>
  );
};
